import React, { useState, useEffect, useMemo } from 'react';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as VerifiedIcon,
  Image as BannerIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NotificationService from '../../services/NotificationService';
import CreatorService from '../../services/CreatorService';
import ImageDesigner from '../ImageDesigner';
import ProfileCompletionPanel from './ProfileCompletionPanel';
import PublicProfileSharePanel from './PublicProfileSharePanel';
import { preparePhotoForApi } from '../../utils/mediaPrepare';
import { normalizePhotoFromApi } from '../../utils/mediaUtils';
import computeProfileCompletion from '../../utils/profileCompletion';
import { SOCIAL_NETWORKS, emptySocialLinks, normalizeSocialLinks } from '../../utils/socialLinks';

const APP_URL = process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_APP_URL || 'http://app.local.dev:8080';

const PROFILE_IMAGE_FORMAT_HINT =
  'Carré (1:1) — affiché sur votre fiche artisan publique. Taille idéale : 512 × 512 px.';

const BANNER_IMAGE_FORMAT_HINT =
  'Paysage (3:1) — bannière pleine largeur sur votre fiche artisan. Taille idéale : 1200 × 400 px.';

const CreatorInfo = () => {
  const navigate = useNavigate();

  const [creatorData, setCreatorData] = useState({
    businessName: '',
    siret: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    phone: '',
    website: '',
    socialLinks: emptySocialLinks(),
    description: '',
    bio: '',
    story: '',
    knowHow: '',
    materials: '',
    techniques: '',
  });

  const [profileImage, setProfileImage] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [activeProductsCount, setActiveProductsCount] = useState(0);
  const [shopSlug, setShopSlug] = useState(null);
  const [directPurchase, setDirectPurchase] = useState(false);

  const [creatorStatus, setCreatorStatus] = useState({
    isVerified: false,
    cguAccepted: false,
    cguAcceptedAt: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    loadCreatorData();
  }, []);

  const loadCreatorData = async () => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const data = await CreatorService.getCreatorInfo();
      setCreatorData({
        businessName: data.businessName || '',
        siret: data.siret || '',
        address: data.address || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        country: data.country || 'France',
        phone: data.phone || '',
        website: data.website || '',
        socialLinks: normalizeSocialLinks(data.socialLinks),
        description: data.description || '',
        bio: data.bio || '',
        story: data.story || '',
        knowHow: data.knowHow || '',
        materials: data.materials || '',
        techniques: data.techniques || '',
      });
      setProfileImage(
        data.profileImage ? normalizePhotoFromApi(data.profileImage) : null
      );
      setBannerImage(
        data.bannerImage ? normalizePhotoFromApi(data.bannerImage) : null
      );
      setActiveProductsCount(data.activeProductsCount ?? 0);
      setShopSlug(data.shopSlug || null);
      setDirectPurchase(Boolean(data.directPurchase));
      setCreatorStatus({
        isVerified: Boolean(data.isVerified),
        cguAccepted: Boolean(data.cguAccepted),
        cguAcceptedAt: data.cguAcceptedAt,
      });
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);

      if (error.response?.status === 401) {
        setAuthError({
          type: 'auth',
          message: 'Votre session a expiré. Veuillez vous reconnecter.',
        });
      } else if (error.response?.status === 403) {
        setAuthError({
          type: 'forbidden',
          message:
            error.response?.data?.error ||
            "Vous n'avez pas les droits pour accéder à cette ressource.",
        });
      } else {
        NotificationService.error('Erreur lors du chargement des données');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCreatorData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setHasChanges(true);
  };

  const handleSocialLinkChange = (networkId, value) => {
    setCreatorData((prev) => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [networkId]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleProfileImageChange = (image) => {
    setProfileImage(image);
    setHasChanges(true);
  };

  const handleBannerImageChange = (image) => {
    setBannerImage(image);
    setHasChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const [preparedProfileImage, preparedBannerImage] = await Promise.all([
        preparePhotoForApi(profileImage),
        preparePhotoForApi(bannerImage),
      ]);
      const data = await CreatorService.updateCreatorInfo({
        ...creatorData,
        profileImage: preparedProfileImage,
        bannerImage: preparedBannerImage,
      });
      setCreatorData({
        businessName: data.businessName || '',
        siret: data.siret || '',
        address: data.address || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        country: data.country || 'France',
        phone: data.phone || '',
        website: data.website || '',
        socialLinks: normalizeSocialLinks(data.socialLinks),
        description: data.description || '',
        bio: data.bio || '',
        story: data.story || '',
        knowHow: data.knowHow || '',
        materials: data.materials || '',
        techniques: data.techniques || '',
      });
      setProfileImage(
        data.profileImage ? normalizePhotoFromApi(data.profileImage) : null
      );
      setBannerImage(
        data.bannerImage ? normalizePhotoFromApi(data.bannerImage) : null
      );
      setActiveProductsCount(data.activeProductsCount ?? activeProductsCount);
      setShopSlug(data.shopSlug || shopSlug);
      setDirectPurchase(Boolean(data.directPurchase));
      setCreatorStatus({
        isVerified: Boolean(data.isVerified),
        cguAccepted: Boolean(data.cguAccepted),
        cguAcceptedAt: data.cguAcceptedAt,
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);

      if (error.response?.status === 401) {
        setAuthError({
          type: 'auth',
          message: 'Votre session a expiré. Veuillez vous reconnecter.',
        });
      } else if (error.response?.status === 403) {
        setAuthError({
          type: 'forbidden',
          message:
            error.response?.data?.error ||
            "Vous n'avez pas les droits pour modifier cette ressource.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const profileCompletion = useMemo(
    () =>
      computeProfileCompletion({
        profileImage,
        creatorData,
        activeProductsCount,
      }),
    [profileImage, creatorData, activeProductsCount]
  );

  const publicProfileUrl = shopSlug ? `${APP_URL}/createurs/${shopSlug}` : null;

  if (isLoading && !hasChanges) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <ErrorIcon style={{ fontSize: 48, color: '#dc2626', marginBottom: 16 }} />
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {authError.type === 'auth' ? 'Session expirée' : 'Accès refusé'}
          </h2>
          <p className="text-gray-600 mb-4">{authError.message}</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Retour au tableau de bord
            </button>
            {authError.type === 'auth' && (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Se reconnecter
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <PersonIcon className="mr-3 text-blue-600" style={{ fontSize: 32 }} />
          <h2 className="text-2xl font-bold">Informations du créateur</h2>
        </div>

        {/* Badge de vérification */}
        <div
          className={`flex items-center px-3 py-1 rounded-full ${
            creatorStatus.isVerified
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {creatorStatus.isVerified ? (
            <>
              <VerifiedIcon fontSize="small" className="mr-1" />
              <span className="text-sm font-medium">Vérifié</span>
            </>
          ) : (
            <>
              <ErrorIcon fontSize="small" className="mr-1" />
              <span className="text-sm font-medium">En attente de vérification</span>
            </>
          )}
        </div>
      </div>

      <ProfileCompletionPanel completion={profileCompletion} />
      <PublicProfileSharePanel publicProfileUrl={publicProfileUrl} />

      {!directPurchase && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Votre boutique est en mise en relation : les visiteurs voient vos contacts et vos liens
          d&apos;achat externes.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <PersonIcon className="mr-2 text-gray-600" />
            Photo de profil
          </h3>
          <p className="text-sm text-gray-500 mb-4">{PROFILE_IMAGE_FORMAT_HINT}</p>
          <ImageDesigner
            image={profileImage}
            onImageChange={handleProfileImageChange}
            variant="square"
            formatHint={PROFILE_IMAGE_FORMAT_HINT}
          />
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <BannerIcon className="mr-2 text-gray-600" />
            Bannière du profil
          </h3>
          <p className="text-sm text-gray-500 mb-4">{BANNER_IMAGE_FORMAT_HINT}</p>
          <ImageDesigner
            image={bannerImage}
            onImageChange={handleBannerImageChange}
            variant="banner"
            formatHint={BANNER_IMAGE_FORMAT_HINT}
          />
        </div>

        {/* Informations commerciales */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <BusinessIcon className="mr-2 text-gray-600" />
            Informations commerciales
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="businessName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nom commercial / Raison sociale
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={creatorData.businessName}
                onChange={handleInputChange}
                placeholder="Nom de votre entreprise"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label htmlFor="siret" className="block text-sm font-medium text-gray-700 mb-2">
                Numéro SIRET
              </label>
              <input
                type="text"
                id="siret"
                name="siret"
                value={creatorData.siret}
                onChange={handleInputChange}
                placeholder="123 456 789 00012"
                maxLength={17}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">14 chiffres (optionnel)</p>
            </div>
          </div>
        </div>

        {/* Coordonnées */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <LocationIcon className="mr-2 text-gray-600" />
            Coordonnées
          </h3>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Adresse
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={creatorData.address}
                onChange={handleInputChange}
                placeholder="Numéro et nom de rue"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="postalCode"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Code postal
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={creatorData.postalCode}
                  onChange={handleInputChange}
                  placeholder="75001"
                  maxLength={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  Ville
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={creatorData.city}
                  onChange={handleInputChange}
                  placeholder="Paris"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="country"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Pays
                </label>
                <select
                  id="country"
                  name="country"
                  value={creatorData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="France">France</option>
                  <option value="Belgique">Belgique</option>
                  <option value="Suisse">Suisse</option>
                  <option value="Canada">Canada</option>
                  <option value="Luxembourg">Luxembourg</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="phone"
                  className="flex items-center text-sm font-medium text-gray-700 mb-2"
                >
                  <PhoneIcon className="mr-1 text-gray-500" fontSize="small" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={creatorData.phone}
                  onChange={handleInputChange}
                  placeholder="+33 1 23 45 67 89"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="website"
                  className="flex items-center text-sm font-medium text-gray-700 mb-2"
                >
                  <WebsiteIcon className="mr-1 text-gray-500" fontSize="small" />
                  Site web
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={creatorData.website}
                  onChange={handleInputChange}
                  placeholder="https://www.monsite.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <WebsiteIcon className="mr-2 text-gray-600" />
            Réseaux sociaux
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Ces liens apparaissent sur votre fiche artisan et votre boutique publiques.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SOCIAL_NETWORKS.map((network) => (
              <div key={network.id}>
                <label
                  htmlFor={`social-${network.id}`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {network.label}
                </label>
                <input
                  type="url"
                  id={`social-${network.id}`}
                  value={creatorData.socialLinks[network.id] || ''}
                  onChange={(e) => handleSocialLinkChange(network.id, e.target.value)}
                  placeholder={network.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Description et bio */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <DescriptionIcon className="mr-2 text-gray-600" />
            Présentation
          </h3>

          <div className="space-y-4">
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                Bio courte
              </label>
              <input
                type="text"
                id="bio"
                name="bio"
                value={creatorData.bio}
                onChange={handleInputChange}
                placeholder="Une phrase qui vous décrit"
                maxLength={150}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{creatorData.bio.length}/150 caractères</p>
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description détaillée
              </label>
              <textarea
                id="description"
                name="description"
                value={creatorData.description}
                onChange={handleInputChange}
                placeholder="Décrivez votre activité, votre histoire, vos valeurs..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="flex items-center text-lg font-medium mb-4">
            <DescriptionIcon className="mr-2 text-gray-600" />
            Univers créatif
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Ces informations apparaissent sur votre fiche artisan publique et aident à vous retrouver.
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="story" className="block text-sm font-medium text-gray-700 mb-2">
                Histoire
              </label>
              <textarea
                id="story"
                name="story"
                value={creatorData.story}
                onChange={handleInputChange}
                placeholder="Comment est née votre activité, ce qui vous anime..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div>
              <label htmlFor="knowHow" className="block text-sm font-medium text-gray-700 mb-2">
                Savoir-faire
              </label>
              <textarea
                id="knowHow"
                name="knowHow"
                value={creatorData.knowHow}
                onChange={handleInputChange}
                placeholder="Votre geste, vos apprentissages, ce que vous maîtrisez..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            <div>
              <label htmlFor="materials" className="block text-sm font-medium text-gray-700 mb-2">
                Matériaux
              </label>
              <input
                type="text"
                id="materials"
                name="materials"
                value={creatorData.materials}
                onChange={handleInputChange}
                placeholder="Grès, porcelaine, lin…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Séparez les matériaux par des virgules.</p>
            </div>

            <div>
              <label htmlFor="techniques" className="block text-sm font-medium text-gray-700 mb-2">
                Techniques
              </label>
              <input
                type="text"
                id="techniques"
                name="techniques"
                value={creatorData.techniques}
                onChange={handleInputChange}
                placeholder="Tournage, émaillage, tissage…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Séparez les techniques par des virgules.</p>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
            disabled={isLoading}
          >
            <CancelIcon className="mr-2" fontSize="small" />
            Annuler
          </button>

          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center disabled:opacity-50"
            disabled={isLoading || !hasChanges}
          >
            <SaveIcon className="mr-2" fontSize="small" />
            {isLoading ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </form>

      {/* Information CGU */}
      {creatorStatus.cguAccepted && creatorStatus.cguAcceptedAt && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>CGU acceptées le :</strong>{' '}
            {new Date(creatorStatus.cguAcceptedAt).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CreatorInfo;
